/**
 * <video-reel> — Sideshow-style vertical video reel + fullscreen viewer.
 * Cards scroll horizontally; clicking one opens a 9:16 viewer with prev/next
 * nav, play/pause, mute, a title, and a per-video CTA. Keyboard + backdrop close.
 */
if (!customElements.get('video-reel')) {
  class VideoReel extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
      if (!this.cards.length) return;

      this.track = this.querySelector('[data-reel-track]');
      this.viewer = this.querySelector('[data-reel-viewer]');
      this.video = this.querySelector('[data-reel-video]');
      this.posterEl = this.querySelector('[data-reel-stage-poster]');
      this.titleEl = this.querySelector('[data-reel-title]');
      this.ctaEl = this.querySelector('[data-reel-cta]');
      this.current = 0;

      // open from a card
      this.cards.forEach((card, i) => {
        const opener = card.querySelector('[data-reel-open]');
        if (opener) opener.addEventListener('click', () => this.open(i));
      });

      // viewer controls (note: multiple close targets — backdrop + button)
      this.querySelectorAll('[data-reel-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      // close on any click outside the video stage (robust click-away)
      this.viewer.addEventListener('click', (e) => {
        if (!e.target.closest('.video-reel__stage')) this.close();
      });
      this.on('[data-reel-next]', 'click', () => this.go(1));
      this.on('[data-reel-prev]', 'click', () => this.go(-1));
      this.on('[data-reel-playpause]', 'click', () => this.togglePlay());
      this.on('[data-reel-mute]', 'click', () => this.toggleMute());

      // track scroll arrows
      this.on('[data-reel-scroll-next]', 'click', () => this.scrollTrack(1));
      this.on('[data-reel-scroll-prev]', 'click', () => this.scrollTrack(-1));

      this._onKey = (e) => {
        if (this.viewer.hasAttribute('hidden')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.go(1);
        if (e.key === 'ArrowLeft') this.go(-1);
      };
      document.addEventListener('keydown', this._onKey);

      // Portal the viewer to <body> so it's never clipped/constrained by the
      // section's clip-path or any transformed ancestor. Listeners are already
      // bound to the nodes, so they survive the move.
      if (this.viewer && this.viewer.parentElement !== document.body) {
        document.body.appendChild(this.viewer);
      }
    }

    on(sel, evt, fn) {
      const el = this.querySelector(sel);
      if (el) el.addEventListener(evt, fn);
    }

    open(i) {
      this.current = i;
      // guarantee the viewer is a direct child of <body> (un-clipped) when shown
      if (this.viewer.parentElement !== document.body) document.body.appendChild(this.viewer);
      this.load();
      this.viewer.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';
    }

    load() {
      const card = this.cards[this.current];
      const src = card.dataset.video;
      const poster = card.dataset.poster;
      const title = card.dataset.title || '';
      const ctaLabel = card.dataset.ctaLabel;
      const ctaUrl = card.dataset.ctaUrl;

      // poster always available as the stage backdrop/fallback
      if (this.posterEl) this.posterEl.style.backgroundImage = poster ? `url("${poster}")` : 'none';

      if (src) {
        this.video.src = src;
        this.video.style.display = '';
        this.video.muted = false;
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        this.video.removeAttribute('src');
        this.video.load();
        this.video.style.display = 'none';
      }

      if (this.titleEl) this.titleEl.textContent = title;
      if (this.ctaEl) {
        if (ctaLabel && ctaUrl) {
          this.ctaEl.textContent = ctaLabel;
          this.ctaEl.href = ctaUrl;
          this.ctaEl.style.display = '';
        } else {
          this.ctaEl.style.display = 'none';
        }
      }
    }

    go(dir) {
      this.current = (this.current + dir + this.cards.length) % this.cards.length;
      this.load();
    }

    togglePlay() {
      if (!this.video.src) return;
      this.video.paused ? this.video.play() : this.video.pause();
    }

    toggleMute() {
      this.video.muted = !this.video.muted;
    }

    scrollTrack(dir) {
      if (!this.track) return;
      const t = this.track;
      const maxScroll = t.scrollWidth - t.clientWidth;
      const atEnd = t.scrollLeft >= maxScroll - 2;
      const atStart = t.scrollLeft <= 2;
      // Wrap: next-from-end → snap to start; prev-from-start → snap to end.
      if (dir > 0 && atEnd) {
        t.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (dir < 0 && atStart) {
        t.scrollTo({ left: maxScroll, behavior: 'smooth' });
      } else {
        t.scrollBy({ left: dir * t.clientWidth * 0.8, behavior: 'smooth' });
      }
    }

    close() {
      this.viewer.setAttribute('hidden', '');
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      document.documentElement.style.overflow = '';
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      if (this.viewer && this.viewer.parentElement === document.body) this.viewer.remove();
    }
  }
  customElements.define('video-reel', VideoReel);
}
